import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { notificationService } from '../../../services/notificationService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const NotificationPreferencesPanel = ({ preferences, onUpdate }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState(preferences || {});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      setSaving(true);
      setMessage(null);
      await notificationService?.updateNotificationPreferences(user?.id, formData);
      setMessage({ type: 'success', text: 'Préférences enregistrées avec succès' });
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error saving preferences:', error);
      setMessage({ type: 'error', text: 'Erreur lors de l\'enregistrement des préférences' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Email Notifications */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Notifications par Email</h3>
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg border border-border">
            <Checkbox
              id="emailEnabled"
              checked={formData?.emailEnabled !== false}
              onChange={(e) => setFormData({ ...formData, emailEnabled: e?.target?.checked })}
            />
            <div className="flex-1">
              <label htmlFor="emailEnabled" className="font-medium text-foreground cursor-pointer">
                Activer les notifications par email
              </label>
              <p className="text-sm text-muted-foreground mt-1">
                Recevoir toutes les notifications par email
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Notification Types */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Types de Notifications</h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg border border-border">
            <Checkbox
              id="inventoryAlerts"
              checked={formData?.inventoryAlerts !== false}
              onChange={(e) => setFormData({ ...formData, inventoryAlerts: e?.target?.checked })}
              disabled={!formData?.emailEnabled}
            />
            <div className="flex-1">
              <label htmlFor="inventoryAlerts" className="font-medium text-foreground cursor-pointer flex items-center gap-2">
                <Icon name="Package" size={18} className="text-warning" />
                Alertes de Stock
              </label>
              <p className="text-sm text-muted-foreground mt-1">
                Notifications lorsque le stock est bas ou critique
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg border border-border">
            <Checkbox
              id="scheduleAssignments"
              checked={formData?.scheduleAssignments !== false}
              onChange={(e) => setFormData({ ...formData, scheduleAssignments: e?.target?.checked })}
              disabled={!formData?.emailEnabled}
            />
            <div className="flex-1">
              <label htmlFor="scheduleAssignments" className="font-medium text-foreground cursor-pointer flex items-center gap-2">
                <Icon name="Calendar" size={18} className="text-primary" />
                Assignations de Mission
              </label>
              <p className="text-sm text-muted-foreground mt-1">
                Notifications lors de nouvelles assignations d'équipe
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg border border-border">
            <Checkbox
              id="scheduleReminders"
              checked={formData?.scheduleReminders !== false}
              onChange={(e) => setFormData({ ...formData, scheduleReminders: e?.target?.checked })}
              disabled={!formData?.emailEnabled}
            />
            <div className="flex-1">
              <label htmlFor="scheduleReminders" className="font-medium text-foreground cursor-pointer flex items-center gap-2">
                <Icon name="Clock" size={18} className="text-trust" />
                Rappels de Mission
              </label>
              <p className="text-sm text-muted-foreground mt-1">
                Rappels avant les missions planifiées
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg border border-border">
            <Checkbox
              id="urgentAlerts"
              checked={formData?.urgentAlerts !== false}
              onChange={(e) => setFormData({ ...formData, urgentAlerts: e?.target?.checked })}
              disabled={!formData?.emailEnabled}
            />
            <div className="flex-1">
              <label htmlFor="urgentAlerts" className="font-medium text-foreground cursor-pointer flex items-center gap-2">
                <Icon name="AlertTriangle" size={18} className="text-error" />
                Alertes Urgentes
              </label>
              <p className="text-sm text-muted-foreground mt-1">
                Notifications critiques nécessitant une action immédiate
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Delivery Frequency */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Fréquence de Livraison</h3>
        <Select
          label="Fréquence"
          value={formData?.digestFrequency || 'immediate'}
          onChange={(e) => setFormData({ ...formData, digestFrequency: e?.target?.value })}
          disabled={!formData?.emailEnabled}
        >
          <option value="immediate">Immédiat (temps réel)</option>
          <option value="daily">Résumé quotidien</option>
          <option value="weekly">Résumé hebdomadaire</option>
        </Select>
        <p className="text-sm text-muted-foreground mt-2">
          Choisissez la fréquence à laquelle vous souhaitez recevoir les notifications
        </p>
      </div>
      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message?.type === 'success' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
        }`}>
          <div className="flex items-center gap-2">
            <Icon name={message?.type === 'success' ? 'CheckCircle' : 'XCircle'} size={20} />
            <span>{message?.text}</span>
          </div>
        </div>
      )}
      {/* Submit Button */}
      <div className="flex justify-end">
        <Button type="submit" disabled={saving}>
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Enregistrement...
            </>
          ) : (
            <>
              <Icon name="Save" size={20} className="mr-2" />
              Enregistrer les Préférences
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default NotificationPreferencesPanel;